import { Member, PointsHistory } from '../types';
import { format, subDays } from 'date-fns';
import { generateUUID } from '../utils/uuid';

/**
 * Trims points history to keep only records from the last 7 days.
 * Does NOT affect totalPoints.
 */
export const trimOldPointsHistory = (member: Member): Member => {
    const cutoff = subDays(new Date(), 7).getTime();
    const currentHistory = member.pointsHistory || [];
    const trimmed = currentHistory.filter(record => record.timestamp >= cutoff);
    if (trimmed.length === currentHistory.length) return member;
    return { ...member, pointsHistory: trimmed };
};

/**
 * Creates a new points history record
 */
export const createPointsHistory = (
    memberId: string,
    amount: number,
    type: PointsHistory['type'],
    options?: {
        reason?: string;
        habitId?: string;
        habitName?: string;
        date?: string;
    }
): PointsHistory => {
    const now = new Date();
    return {
        id: generateUUID(),
        memberId,
        amount,
        type,
        reason: options?.reason,
        habitId: options?.habitId,
        habitName: options?.habitName,
        date: options?.date || format(now, 'yyyy-MM-dd'),
        timestamp: now.getTime(),
    };
};

/**
 * Adds a points history record to a member
 */
export const addPointsHistory = (
    member: Member,
    history: PointsHistory
): Member => {
    const currentHistory = member.pointsHistory || [];
    return {
        ...member,
        pointsHistory: [...currentHistory, history],
    };
};

/**
 * Updates member points and adds history record
 */
export const updateMemberPoints = (
    member: Member,
    amount: number,
    type: PointsHistory['type'],
    options?: {
        reason?: string;
        habitId?: string;
        habitName?: string;
        date?: string;
    }
): Member => {
    const newTotalPoints = Math.max(0, (member.totalPoints || 0) + amount);
    const historyRecord = createPointsHistory(member.id, amount, type, options);

    const updated: Member = {
        ...member,
        totalPoints: newTotalPoints,
        pointsHistory: [...(member.pointsHistory || []), historyRecord],
    };

    return trimOldPointsHistory(updated);
};

/**
 * Gets points history for a specific date range
 */
export const getPointsHistoryByDateRange = (
    member: Member,
    startDate: Date,
    endDate: Date
): PointsHistory[] => {
    const history = member.pointsHistory || [];
    const startTime = startDate.getTime();
    const endTime = endDate.getTime();

    return history.filter(
        (record) => record.timestamp >= startTime && record.timestamp <= endTime
    );
};

/**
 * Gets points history by type
 */
export const getPointsHistoryByType = (
    member: Member,
    type: PointsHistory['type']
): PointsHistory[] => {
    const history = member.pointsHistory || [];
    return history.filter((record) => record.type === type);
};

/**
 * Calculates total points earned/spent by type
 */
export const calculatePointsByType = (
    member: Member,
    type: PointsHistory['type']
): { earned: number; spent: number } => {
    const history = getPointsHistoryByType(member, type);

    const earned = history
        .filter((record) => record.amount > 0)
        .reduce((sum, record) => sum + record.amount, 0);

    const spent = Math.abs(
        history
            .filter((record) => record.amount < 0)
            .reduce((sum, record) => sum + record.amount, 0)
    );

    return { earned, spent };
};
